import { cartIcon, giftIcon } from '@/assets';
import { type Gift, type Recipient } from '@shared/trpc/schemas';

interface GiftGridProps {
  recipients: Recipient[];
  showCart: boolean;
  token: string; // Add token prop for tracking API calls
  onAddToCart: (gift: Gift) => void;
  onImageClick: (imageUrl: string, title: string) => void;
}

export function GiftGrid({ recipients, showCart, token, onAddToCart, onImageClick }: GiftGridProps) {
  // Handle "Buy It Myself" click with tracking
  const handleBuyItMyself = async (gift: Gift) => {
    try {
      // Track the click (don't await to avoid blocking the URL opening)
      fetch(`/api/public/track-buy-myself/${token}/${gift.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }).catch(error => {
        console.error('Failed to track Buy It Myself:', error);
      });
      
      // Open the gift URL immediately (don't wait for tracking)
      window.open(gift.url, '_blank');
    } catch (error) {
      console.error('Error handling Buy It Myself:', error);
      // Still open the URL even if tracking fails
      window.open(gift.url, '_blank');
    }
  };

  // Helper function to determine optimal grid columns based on gift count and cart visibility
  const getGridColumns = (giftCount: number, isCartVisible: boolean) => {
    return isCartVisible ? 'grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';
    // if (giftCount === 1) {
    //   // Single item: center it with a max-width container
    //   return isCartVisible ? 'grid-cols-1 max-w-md mx-auto' : 'grid-cols-1 max-w-lg mx-auto';
    // } else if (giftCount === 2) {
    //   // Two items: responsive based on cart state
    //   return isCartVisible
    //     ? 'grid-cols-1 xl:grid-cols-2' // With cart: single column until 1280px, then 2 columns
    //     : 'grid-cols-1 md:grid-cols-2'; // Without cart: 2 columns from 768px
    // } else {
    //   // Three or more items: responsive grid
    //   return isCartVisible
    //     ? 'grid-cols-1 xl:grid-cols-2' // With cart: single column until 1280px, then 2 columns
    //     : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'; // Without cart: max 3 columns
    // }
  };

  return (
    <div>
      {recipients.map((recipient) => (
        <div key={recipient.id} id={`recipient-${recipient.id}`} className="mb-12">
          {/* Recipient Header */}
          <div className="flex items-center mb-14">
            <img src={giftIcon} alt="gift" className="mr-2" />
            <h2 className="text-[32px] font-light text-purple-medium leading-[1.2]">Gifts for <span className="font-semibold">{recipient.name}</span></h2>
          </div>

          {/* Gift Grid */}
          <div className={`grid ${getGridColumns(recipient.gifts.length, showCart)} gap-6 gap-y-[48px]`}>
            {recipient.gifts.map((gift) => {
              const isProcessed = gift.let_birdie_handle_it;
              // Only disable when "Let Birdie Handle It" was processed (payment completed)
              // "Buy It Myself" doesn't disable - user can still change their mind
              const isDisabled = isProcessed;
              
              return (
              <div
                key={gift.id}
                className={`rounded-2xl border p-8 w-[380px] mx-auto transition-all duration-300 flex flex-col ${
                  isDisabled 
                    ? 'bg-gray-100 border-gray-300 opacity-75' 
                    : 'bg-[#f6f6f6] border-[#f3c8d1]'
                }`}
                style={{ 
                  boxShadow: isDisabled 
                    ? '0px 2px 10px 0px rgba(0, 0, 0, 0.1)' 
                    : '0px 4px 20px 0px rgba(21, 93, 226, 0.18)' 
                }}
              >
                {/* Gift Image */}
                {(gift.image_url || gift.image_data) ? (
                  <div
                    className="w-full h-[182px] cursor-pointer hover:opacity-90 transition-opacity relative"
                    onClick={() => onImageClick(gift.image_data || gift.image_url || '', gift.title)}
                  >
                    <img
                      src={gift.image_data || gift.image_url || ''}
                      alt={gift.title}
                      className="rounded-2xl w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    {/* Magnifying glass icon positioned at bottom right of image */}
                    <div 
                      className="absolute bottom-2 right-2 rounded-full p-1 hover:opacity-80 transition-all"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 22 22" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        className="cursor-pointer"
                      >
                        <mask id="mask0_373_2600" maskUnits="userSpaceOnUse" x="0" y="0" width="22" height="22">
                          <rect width="22" height="22" fill="#D9D9D9"/>
                        </mask>
                        <g mask="url(#mask0_373_2600)">
                          <path d="M17.9667 19.25L12.1917 13.475C11.7333 13.8417 11.2063 14.1319 10.6104 14.3458C10.0146 14.5597 9.38056 14.6667 8.70833 14.6667C7.04306 14.6667 5.63368 14.0899 4.48021 12.9365C3.32674 11.783 2.75 10.3736 2.75 8.70833C2.75 7.04306 3.32674 5.63368 4.48021 4.48021C5.63368 3.32674 7.04306 2.75 8.70833 2.75C10.3736 2.75 11.783 3.32674 12.9365 4.48021C14.0899 5.63368 14.6667 7.04306 14.6667 8.70833C14.6667 9.38056 14.5597 10.0146 14.3458 10.6104C14.1319 11.2063 13.8417 11.7333 13.475 12.1917L19.25 17.9667L17.9667 19.25ZM8.70833 12.8333C9.85417 12.8333 10.8281 12.4323 11.6302 11.6302C12.4323 10.8281 12.8333 9.85417 12.8333 8.70833C12.8333 7.5625 12.4323 6.58854 11.6302 5.78646C10.8281 4.98438 9.85417 4.58333 8.70833 4.58333C7.5625 4.58333 6.58854 4.98438 5.78646 5.78646C4.98438 6.58854 4.58333 7.5625 4.58333 8.70833C4.58333 9.85417 4.98438 10.8281 5.78646 11.6302C6.58854 12.4323 7.5625 12.8333 8.70833 12.8333ZM7.79167 11.4583V9.625H5.95833V7.79167H7.79167V5.95833H9.625V7.79167H11.4583V9.625H9.625V11.4583H7.79167Z" fill="black" fillOpacity="0.7"/>
                        </g>
                      </svg>
                    </div>
                  </div>
                ): <div className="w-full h-[182px] bg-gray-200 rounded-2xl"></div>}

                {/* Gift Title - Max 2 lines */}
                <div className="mt-4 h-[62px] flex items-start">
                  <h3 className="text-[24px] font-bold leading-[26px] text-[#2d2d2d] line-clamp-2">
                    {gift.title}
                  </h3>
                </div>

                {/* Gift Description - Max 3 lines */}
                <div className="mt-2 h-[72px] flex items-start">
                  {gift.description && (
                    <p className="text-base leading-[1.5] text-[#6b7280] line-clamp-3">
                      {gift.description}
                    </p>
                  )}
                </div>

                {/* Price - Max 2 lines */}
                <div className="mt-4 h-[80px] flex items-center border-dashed border-y-[0.8px] border-[#DBCFDD] py-4">
                  {gift.price && (
                    <div className="text-purple-medium font-bold text-[16px] line-clamp-2 italic">
                      {gift.price}
                    </div>
                  )}
                </div>
                
                {/* Action Buttons - Fixed at bottom */}
                <div className="mt-auto pt-6">
                  <div className="space-y-4">
                    {isProcessed ? (
                      // Show status for processed gifts
                      <div className="text-center py-4">
                        <div className="text-green-600 font-semibold mb-1">✓ Being Processed</div>
                        <div className="text-sm text-gray-600">We'll contact you in the next few days</div>
                      </div>
                    ) : (
                      // Show normal buttons for available gifts
                      <>
                        <button 
                          onClick={() => handleBuyItMyself(gift)}
                          className="w-full border border-footer py-[16.5px] text-[#3d4e30] text-lg rounded-full font-semibold hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                        >
                          BUY IT MYSELF
                        </button>

                        <button
                          onClick={() => onAddToCart(gift)}
                          className="flex w-full justify-center bg-[#3d4e30] text-lg rounded-full font-semibold py-[16.5px] text-white hover:bg-green-dark transition-all duration-200 cursor-pointer"
                        >
                          <img src={cartIcon} className="mr-2" /><span>LET BIRDIE HANDLE IT</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}